// Keys whose numeric value is a 0–1 fraction rendered as a percentage.
const FRACTION_KEYS = new Set([
  "burnable_fraction",
  "non_burnable_fraction",
  "area_share",
  "risk",
  "floor_bonus",
]);
// Keys whose numeric value is already a 0–100 percentage.
const PERCENT_KEYS = new Set(["national_percentile"]);

const RISK_CONFIG = {
  fire: {
    fields: [
      "radius_m",
      "circle_area_m2",
      "burnable_area_m2",
      "non_burnable_area_m2",
      "burnable_fraction",
      "non_burnable_fraction",
      "risk_category_label",
    ],
    // Per-polygon fields for area assessments (from `polygon_results`).
    // `message` is omitted: for fire it equals `risk_category_label`.
    polygonFields: [
      "risk",
      "buffer_area_m2",
      "burnable_area_m2",
      "non_burnable_area_m2",
      "burnable_fraction",
      "non_burnable_fraction",
      "risk_category_label",
    ],
    hasClassBreakdown: true,
  },
  flood: {
    fields: [
      "polygon_area_m2",
      "in_flood_zone",
      "flood_zone_name",
      "flood_zone_area_km2",
      "slope_percent",
      "elevation_m",
      "elevation_min",
      "elevation_max",
      "slope_pct",
      "floor",
    ],
    polygonFields: [
      "risk",
      "message",
      "polygon_area_m2",
      "elevation_min",
      "elevation_max",
    ],
    hasBinCoverage: true,
  },
  earthquake: {
    fields: [
      "polygon_area_m2",
      "national_percentile",
      "national_percentile_description",
      "pga_g",
      "pga_risk_explainability",
    ],
    polygonFields: [
      "risk",
      "message",
      "pga_g",
      "national_percentile",
      "polygon_area_m2",
    ],
    hasBinCoverage: true,
  },
};

const DEFAULT_LOCALE = "en";

const asString = (value) =>
  String(value).replace(/_/g, " ").replace(/\s+/g, " ").trim();

const toTitleCase = (value) =>
  asString(value).replace(/\b\w/g, (letter) => letter.toUpperCase());

const getTranslator = (translate) =>
  typeof translate === "function" ? translate : (_, fallback) => fallback;

const getFieldLabel = (key, translate) =>
  translate(`descriptionDict.fields.${key}`, toTitleCase(key));

const getSectionLabel = (key, translate) =>
  translate(`descriptionDict.sections.${key}`, toTitleCase(key));

const getBucketLabel = (bucket, translate) =>
  translate(`descriptionDict.buckets.${bucket}`, toTitleCase(bucket));

const toComponentKey = (component) => asString(component).toLowerCase();

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const formatNumber = (value, locale, maximumFractionDigits = 3) =>
  new Intl.NumberFormat(locale || DEFAULT_LOCALE, {
    maximumFractionDigits,
  }).format(value);

const interpolateTemplate = (template, params = {}) =>
  String(template || "").replace(/\{\{\s*([^}\s]+)\s*\}\}/g, (_, key) =>
    Object.prototype.hasOwnProperty.call(params, key) ? params[key] : "",
  );

const isFloodReturnPeriodKey = (key) => /^\d+$/.test(String(key || "").trim());

const formatFloodDepthValue = (value, locale, translate) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "";
  const formatFloodNumber = (nextValue) => formatNumber(nextValue, locale, 2);

  if (numericValue < 0.01) {
    const unit = translate("descriptionDict.units.mm", "mm");
    return `${formatFloodNumber(numericValue * 1000)} ${unit}`;
  }

  if (numericValue < 1) {
    const unit = translate("descriptionDict.units.cm", "cm");
    return `${formatFloodNumber(numericValue * 100)} ${unit}`;
  }

  const unit = translate("descriptionDict.units.m", "m");
  return `${formatFloodNumber(numericValue)} ${unit}`;
};

export const formatDisplayValue = (key, value, locale, translate) => {
  if (!hasValue(value)) return "";
  if (typeof value === "boolean") {
    return value
      ? translate("common.yes", "Yes")
      : translate("common.no", "No");
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    if (FRACTION_KEYS.has(key)) {
      return `${formatNumber(numericValue * 100, locale)}%`;
    }
    if (PERCENT_KEYS.has(key)) {
      return `${formatNumber(numericValue, locale)}%`;
    }
    return formatNumber(numericValue, locale);
  }

  return String(value);
};

const buildFields = ({
  dict,
  fields,
  locale,
  translate,
  floodBucketsByComponent,
}) =>
  fields
    .map((key) => {
      const baseValue = formatDisplayValue(key, dict[key], locale, translate);
      const floodBucket = floodBucketsByComponent?.[key];
      const bucketSuffix = floodBucket
        ? ` (${getBucketLabel(floodBucket, translate)})`
        : "";

      return {
        label: getFieldLabel(key, translate),
        value: `${baseValue}${bucketSuffix}`.trim(),
      };
    })
    .filter(({ value }) => hasValue(value));

const buildFireClassBreakdown = ({ dict, locale, translate }) => {
  const classes = Array.isArray(dict?.class_breakdown)
    ? dict.class_breakdown
    : [];
  if (!classes.length) return [];

  return classes
    .map((item, index) => {
      const classLabel = translate("descriptionDict.fields.class", "Class");
      const title =
        item?.class_name || item?.class_id
          ? `${item?.class_name || `${classLabel} ${item?.class_id}`}`
          : `${classLabel} ${index + 1}`;

      const rows = buildFields({
        dict: item || {},
        fields: ["area_share", "comment"],
        locale,
        translate,
      });

      return { title, rows };
    })
    .filter((item) => item.rows.length || hasValue(item.title));
};

const isSinglePolygonAssessment = (dict) => dict?.input_type === "polygon";
const isMultiplePolygonAssessment = (dict) => dict?.input_type === "polygons";

// True when the description dict comes from an area (polygon) assessment.
const isAreaAssessment = (dict) =>
  isSinglePolygonAssessment(dict) || isMultiplePolygonAssessment(dict);

// `bin_coverage_pct` values are already on a 0–100 scale. Format with
// significant digits (not fixed decimals) so tiny buckets keep their figures —
// 0.0054 reads as 0.0054% instead of collapsing to 0% — while mid-range values
// stay concise (32.8267 -> 32.83%).
const formatCoveragePercent = (value, locale) =>
  `${new Intl.NumberFormat(locale || DEFAULT_LOCALE, {
    maximumSignificantDigits: 4,
  }).format(Number(value))}%`;

// Turn a `bin_coverage_pct` object into a single breakdown item, keeping only
// buckets with actual coverage (> 0%).
const buildBinCoverageItem = ({ coverage, locale, translate }) => {
  if (!coverage || typeof coverage !== "object") return null;

  const rows = Object.entries(coverage)
    .filter(([, value]) => Number(value) > 0)
    .map(([bucket, value]) => ({
      label: getBucketLabel(bucket, translate),
      value: formatCoveragePercent(value, locale),
    }));

  if (!rows.length) return null;

  return {
    title: getSectionLabel("zoneCoverage", translate),
    rows,
  };
};

// One section per polygon for area assessments, mirroring the point-assessment
// fields plus per-polygon class breakdown (fire) or zone coverage (flood/quake).
const buildPolygonSections = ({ riskType, dict, locale, translate }) => {
  const config = RISK_CONFIG[riskType];
  const results = Array.isArray(dict?.polygon_results)
    ? dict.polygon_results
    : [];
  if (!config?.polygonFields || !results.length) return [];

  const titleTemplate = translate(
    "descriptionDict.fields.polygonTitle",
    "Polygon {{index}}",
  );

  return results
    .map((result, index) => {
      const polygon = result || {};
      const rows = buildFields({
        dict: polygon,
        fields: config.polygonFields,
        locale,
        translate,
      });

      const items = [];
      if (config.hasClassBreakdown) {
        items.push(
          ...buildFireClassBreakdown({ dict: polygon, locale, translate }),
        );
      }
      if (config.hasBinCoverage) {
        const coverageItem = buildBinCoverageItem({
          coverage: polygon.bin_coverage_pct,
          locale,
          translate,
        });
        if (coverageItem) items.push(coverageItem);
      }

      const displayIndex = Number.isFinite(Number(polygon.index))
        ? Number(polygon.index) + 1
        : index + 1;

      return {
        title: interpolateTemplate(titleTemplate, { index: displayIndex }),
        rows,
        items,
      };
    })
    .filter((section) => section.rows.length || section.items.length);
};

const buildFloodBucketMapByField = (dict) => {
  const components = Array.isArray(dict?.components) ? dict.components : [];
  const floorBucketFromTopLevel = hasValue(dict?.floor_bucket)
    ? String(dict.floor_bucket)
    : null;
  if (!components.length) {
    return floorBucketFromTopLevel ? { floor: floorBucketFromTopLevel } : {};
  }

  const componentToFieldMap = {
    flood_zone: "in_flood_zone",
    slope: "slope_percent",
    elevation: "elevation_m",
    floor: "floor",
  };

  const bucketsByField = components.reduce((acc, item) => {
    const componentKey = toComponentKey(item?.component);
    const fieldKey = componentToFieldMap[componentKey];
    if (!fieldKey || !hasValue(item?.bucket)) return acc;
    acc[fieldKey] = String(item.bucket);
    return acc;
  }, {});

  if (floorBucketFromTopLevel) {
    // Prefer explicit top-level floor bucket when available.
    bucketsByField.floor = floorBucketFromTopLevel;
  }

  return bucketsByField;
};

const buildEarthquakeMethodology = ({ dict, locale, translate }) => {
  const methodology = dict?.methodology;
  if (!methodology || typeof methodology !== "object") return [];

  return buildFields({
    dict: methodology,
    fields: ["pga_definition", "risk_formula", "percentile_formula"],
    locale,
    translate,
  });
};

const buildFloodReturnPeriodRows = ({ dict, locale, translate }) => {
  if (!dict || typeof dict !== "object") return [];

  const labelTemplate = translate(
    "descriptionDict.fields.floodReturnPeriodYears",
    "{{years}}-year",
  );

  return Object.entries(dict)
    .filter(([key, value]) => isFloodReturnPeriodKey(key) && hasValue(value))
    .sort(([left], [right]) => Number(left) - Number(right))
    .map(([years, depthMeters]) => {
      const yearsLabel = formatNumber(Number(years), locale);
      const label = interpolateTemplate(labelTemplate, { years: yearsLabel });

      return {
        label,
        value: formatFloodDepthValue(depthMeters, locale, translate),
      };
    })
    .filter(({ value }) => hasValue(value));
};

export const buildRiskDescriptionSections = ({
  riskType,
  descriptionDict,
  risk,
  locale,
  translate,
}) => {
  if (!descriptionDict || typeof descriptionDict !== "object") return [];
  const t = getTranslator(translate);

  const config = RISK_CONFIG[riskType];
  if (!config) return [];

  const sections = [];
  const floodBucketsByField =
    riskType === "flood" ? buildFloodBucketMapByField(descriptionDict) : null;
  const topLevelRows = buildFields({
    dict: descriptionDict,
    fields: config.fields,
    locale,
    translate: t,
    floodBucketsByComponent: floodBucketsByField,
  });

  if (riskType === "flood" && Number.isFinite(Number(risk)) && risk != null) {
    topLevelRows.unshift({
      label: t("descriptionDict.fields.preciseRisk", "Precise risk"),
      value: `${formatNumber(Number(risk) * 100, locale, 3)}%`,
    });
  }
  const floodReturnPeriodRows =
    riskType === "flood"
      ? buildFloodReturnPeriodRows({
          dict: descriptionDict,
          locale,
          translate: t,
        })
      : [];

  if (topLevelRows.length) {
    sections.push({ title: null, rows: topLevelRows, items: [] });
  }

  if (floodReturnPeriodRows.length) {
    sections.push({
      title: getSectionLabel("returnPeriods", t),
      valueTitle: getSectionLabel("maximumPossibleDepth", t),
      rows: floodReturnPeriodRows,
      items: [],
      note: t("descriptionDict.notes.floodReturnPeriodDisclaimer"),
    });
  }

  if (riskType === "fire") {
    const items = buildFireClassBreakdown({
      dict: descriptionDict,
      locale,
      translate: t,
    });
    if (items.length) {
      sections.push({
        title: getSectionLabel("classBreakdown", t),
        rows: [],
        items,
      });
    }
  }

  if (riskType === "earthquake") {
    const methodologyRows = buildEarthquakeMethodology({
      dict: descriptionDict,
      locale,
      translate: t,
    });
    if (methodologyRows.length) {
      sections.push({
        title: getSectionLabel("methodology", t),
        rows: methodologyRows,
        items: [],
      });
    }
  }

  // Area assessments carry extra per-polygon data. Point assessments have
  // neither `input_type: "polygons"` nor `polygon_results`, so they are
  // unaffected by everything below.
  if (isAreaAssessment(descriptionDict)) {
    const config = RISK_CONFIG[riskType];

    // Aggregate zone coverage (flood/earthquake) — always shown for areas so
    // single-polygon assessments still surface coverage.
    if (config?.hasBinCoverage) {
      const coverageItem = buildBinCoverageItem({
        coverage: descriptionDict.bin_coverage_pct,
        locale,
        translate: t,
      });
      if (coverageItem) {
        sections.push({
          title: getSectionLabel("zoneCoverage", t),
          rows: coverageItem.rows,
          items: [],
        });
      }
    }

    // Per-polygon breakdown — only when there is more than one polygon, since
    // the aggregate block above already mirrors a single polygon.
    const polygonResults = Array.isArray(descriptionDict.polygon_results)
      ? descriptionDict.polygon_results
      : [];
    if (polygonResults.length > 1) {
      sections.push(
        ...buildPolygonSections({
          riskType,
          dict: descriptionDict,
          locale,
          translate: t,
        }),
      );
    }
  }

  return sections.filter(
    (section) => section.rows.length || section.items.length,
  );
};

// --- UI-only redesigned view -------------------------------------------------
// `buildRiskDescriptionView` powers the React `RiskDescriptionContent`. It keeps
// `buildRiskDescriptionSections` (used by the PDF export) untouched and instead
// returns chart-ready raw data: a `basicRows` block always shown up front, plus
// `groups` (pie / bar / table / per-polygon) rendered inside collapsibles.
// Slices carry the raw `class_id` / bucket so the chart components own coloring.

const buildFireClassPie = ({ dict, locale, translate }) => {
  const classes = Array.isArray(dict?.class_breakdown)
    ? dict.class_breakdown
    : [];
  const classLabel = translate("descriptionDict.fields.class", "Class");

  const slices = classes
    .map((item, index) => {
      const value = Number(item?.area_share);
      if (!Number.isFinite(value) || value <= 0) return null;
      const label =
        item?.class_name ||
        `${classLabel} ${item?.class_id ?? index + 1}`.trim();
      return {
        id: item?.class_id ?? `class-${index}`,
        classId: item?.class_id,
        label,
        value,
        comment: hasValue(item?.comment) ? String(item.comment) : "",
        share: `${formatNumber(value * 100, locale)}%`,
      };
    })
    .filter(Boolean);

  if (!slices.length) return null;

  return {
    type: "pie",
    key: "classBreakdown",
    title: getSectionLabel("classBreakdown", translate),
    colorMode: "fireClass",
    riskType: "fire",
    slices,
  };
};

const buildZoneCoveragePie = ({ coverage, riskType, locale, translate }) => {
  if (!coverage || typeof coverage !== "object") return null;

  const slices = Object.entries(coverage)
    .filter(([, value]) => Number(value) > 0)
    .map(([bucket, value]) => ({
      id: bucket,
      bucket,
      label: getBucketLabel(bucket, translate),
      value: Number(value),
      share: formatCoveragePercent(value, locale),
    }));

  if (!slices.length) return null;

  return {
    type: "pie",
    key: "zoneCoverage",
    title: getSectionLabel("zoneCoverage", translate),
    colorMode: "zoneCoverage",
    riskType,
    slices,
  };
};

// Per-polygon risks for the RiskItem mini circular-progress row, only on
// multi-polygon assessments. `message` falls back to `risk_category_label`
// (fire polygons carry the label there).
export const getMultiPolygonRisks = (descriptionDict) => {
  if (!isMultiplePolygonAssessment(descriptionDict)) return [];
  const results = Array.isArray(descriptionDict?.polygon_results)
    ? descriptionDict.polygon_results
    : [];

  return results
    .map((polygon, index) => ({
      index: Number.isFinite(Number(polygon?.index))
        ? Number(polygon.index) + 1
        : index + 1,
      risk: Number(polygon?.risk),
      message: polygon?.message ?? polygon?.risk_category_label ?? null,
    }))
    .filter((polygon) => Number.isFinite(polygon.risk));
};

// Compact pie for the RiskItem header, shown only on single-polygon
// assessments: fire shows its class breakdown, flood/earthquake their zone
// coverage. Returns a pie group ({ slices, colorMode, ... }) or null.
export const getSinglePolygonHeaderPie = ({
  riskType,
  descriptionDict,
  locale,
  translate,
}) => {
  if (!isSinglePolygonAssessment(descriptionDict)) return null;
  const config = RISK_CONFIG[riskType];
  if (!config) return null;
  const t = getTranslator(translate);

  if (config.hasClassBreakdown) {
    return buildFireClassPie({ dict: descriptionDict, locale, translate: t });
  }
  if (config.hasBinCoverage) {
    return buildZoneCoveragePie({
      coverage: descriptionDict?.bin_coverage_pct,
      riskType,
      locale,
      translate: t,
    });
  }
  return null;
};

const buildFloodReturnPeriodBars = ({ dict, locale, translate }) => {
  if (!dict || typeof dict !== "object") return null;

  const labelTemplate = translate(
    "descriptionDict.fields.floodReturnPeriodYears",
    "{{years}}-year",
  );

  const bars = Object.entries(dict)
    .filter(([key, value]) => isFloodReturnPeriodKey(key) && hasValue(value))
    .sort(([left], [right]) => Number(left) - Number(right))
    .map(([years, depthMeters]) => {
      const depth = Number(depthMeters);
      if (!Number.isFinite(depth)) return null;
      const yearsLabel = formatNumber(Number(years), locale);
      return {
        period: interpolateTemplate(labelTemplate, { years: yearsLabel }),
        depthMeters: depth,
        displayValue: formatFloodDepthValue(depthMeters, locale, translate),
      };
    })
    .filter(Boolean);

  if (!bars.length) return null;

  return {
    type: "bar",
    key: "returnPeriods",
    title: getSectionLabel("returnPeriods", translate),
    xLabel: getSectionLabel("returnPeriods", translate),
    yLabel: getSectionLabel("maximumPossibleDepth", translate),
    bars,
    note: translate("descriptionDict.notes.floodReturnPeriodDisclaimer"),
  };
};

// Per-polygon chart groups (fire class pie or zone-coverage pie).
const buildPolygonChartGroups = ({
  config,
  polygon,
  riskType,
  locale,
  translate,
}) => {
  const groups = [];
  if (config.hasClassBreakdown) {
    const pie = buildFireClassPie({ dict: polygon, locale, translate });
    if (pie) groups.push(pie);
  }
  if (config.hasBinCoverage) {
    const pie = buildZoneCoveragePie({
      coverage: polygon.bin_coverage_pct,
      riskType,
      locale,
      translate,
    });
    if (pie) groups.push(pie);
  }
  return groups;
};

export const buildRiskDescriptionView = ({
  riskType,
  descriptionDict,
  risk,
  locale,
  translate,
}) => {
  if (!descriptionDict || typeof descriptionDict !== "object") return null;
  const t = getTranslator(translate);

  const config = RISK_CONFIG[riskType];
  if (!config) return null;

  const floodBucketsByField =
    riskType === "flood" ? buildFloodBucketMapByField(descriptionDict) : null;
  const basicRows = buildFields({
    dict: descriptionDict,
    fields: config.fields,
    locale,
    translate: t,
    floodBucketsByComponent: floodBucketsByField,
  });

  if (riskType === "flood" && Number.isFinite(Number(risk)) && risk != null) {
    basicRows.unshift({
      label: t("descriptionDict.fields.preciseRisk", "Precise risk"),
      value: `${formatNumber(Number(risk) * 100, locale, 3)}%`,
    });
  }

  const groups = [];

  // On multi-polygon assessments, suffix each data section's title with its
  // scope so it's clear whether it covers all drawn areas or one specific
  // polygon, e.g. "Class breakdown (All areas)" vs "Class breakdown (Area 1)".
  // Single-polygon and point assessments have no ambiguity, so no suffix.
  const isArea = isAreaAssessment(descriptionDict);
  const allAreasLabel = isMultiplePolygonAssessment(descriptionDict)
    ? t("descriptionDict.scope.allAreas", "(All areas)")
    : "";
  const withTitleSuffix = (group, suffix) =>
    suffix ? { ...group, title: `${group.title} ${suffix}` } : group;

  // The basic-data rows lead the section list. The UI opens this section by
  // default on point assessments and collapses it on area assessments.
  if (basicRows.length) {
    groups.push(
      withTitleSuffix(
        {
          type: "table",
          key: "basicData",
          title: getSectionLabel("basicData", t),
          rows: basicRows,
        },
        allAreasLabel,
      ),
    );
  }

  if (riskType === "fire") {
    const pie = buildFireClassPie({
      dict: descriptionDict,
      locale,
      translate: t,
    });
    if (pie) groups.push(withTitleSuffix(pie, allAreasLabel));
  }

  if (riskType === "flood") {
    const bar = buildFloodReturnPeriodBars({
      dict: descriptionDict,
      locale,
      translate: t,
    });
    if (bar) groups.push(withTitleSuffix(bar, allAreasLabel));
  }

  if (riskType === "earthquake") {
    const methodologyRows = buildEarthquakeMethodology({
      dict: descriptionDict,
      locale,
      translate: t,
    });
    if (methodologyRows.length) {
      groups.push({
        type: "table",
        key: "methodology",
        title: getSectionLabel("methodology", t),
        rows: methodologyRows,
      });
    }
  }

  // Aggregate zone coverage (flood/earthquake) — shown whenever top-level
  // `bin_coverage_pct` exists, including single-polygon assessments.
  if (config.hasBinCoverage) {
    const pie = buildZoneCoveragePie({
      coverage: descriptionDict.bin_coverage_pct,
      riskType,
      locale,
      translate: t,
    });
    if (pie) groups.push(withTitleSuffix(pie, allAreasLabel));
  }

  // Per-polygon breakdown — only when more than one polygon, since the aggregate
  // blocks above already mirror a single polygon.
  const polygonResults = Array.isArray(descriptionDict.polygon_results)
    ? descriptionDict.polygon_results
    : [];
  if (
    isMultiplePolygonAssessment(descriptionDict) &&
    polygonResults.length > 1
  ) {
    const titleTemplate = t(
      "descriptionDict.fields.polygonTitle",
      "Polygon {{index}}",
    );
    const areaScopeTemplate = t(
      "descriptionDict.scope.area",
      "(Area {{index}})",
    );

    polygonResults.forEach((result, index) => {
      const polygon = result || {};
      const polygonBasicRows = buildFields({
        dict: polygon,
        fields: config.polygonFields,
        locale,
        translate: t,
      });
      const polygonGroups = buildPolygonChartGroups({
        config,
        polygon,
        riskType,
        locale,
        translate: t,
      });

      if (!polygonBasicRows.length && !polygonGroups.length) return;

      const displayIndex = Number.isFinite(Number(polygon.index))
        ? Number(polygon.index) + 1
        : index + 1;
      const areaSuffix = interpolateTemplate(areaScopeTemplate, {
        index: displayIndex,
      });

      groups.push({
        type: "polygon",
        key: `polygon-${index}`,
        title: interpolateTemplate(titleTemplate, { index: displayIndex }),
        basicRows: polygonBasicRows,
        groups: polygonGroups.map((child) =>
          withTitleSuffix(child, areaSuffix),
        ),
      });
    });
  }

  if (!groups.length) return null;
  return { groups, isArea };
};

export const getDescriptionFallbackLines = (description) =>
  String(description || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

export const hasRiskDescriptionContent = ({
  description,
  descriptionDict,
  riskType,
}) => {
  const sections = buildRiskDescriptionSections({ riskType, descriptionDict });
  if (sections.length) return true;
  return getDescriptionFallbackLines(description).length > 0;
};
